import { React, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import ImageEditModal from './ImageEditModal';
import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ReactComponent as ImageSvg } from '../../../image/Image.svg';
import WhitePencelIcon from '../../../image/WhitePencelIcon.png';
import { ReactComponent as PlusButtonSvg } from '../../../image/Plus.svg';
import { ReactComponent as ExitButtonSvg } from '../../../image/ExitButton.svg';

let pictureIdSeq = 0;
function makePictureId() {
  pictureIdSeq += 1;
  return `pic-${Date.now()}-${pictureIdSeq}`;
}

const NoticePictureInput = forwardRef((props, ref) => {
  const { initialPictures, readOnly = false } = props;
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [picture, setPicture] = useState(undefined);
  const [pictures, setPictures] = useState([]);
  const [pictureIndex, setPictureIndex] = useState(0);
  const [pictureWidth, setPictureWidth] = useState(0);
  const [pictureHeight, setPictureHeight] = useState(0);
  const [isClickedEditPhoto, setIsClickedEditPhoto] = useState(false);
  const [photoRatios, setPhotoRatios] = useState({});

  function handlePhotoLoad(src, e) {
    const ratio = e.target.naturalWidth / e.target.naturalHeight;
    setPhotoRatios(prev => prev[src] === ratio ? prev : { ...prev, [src]: ratio });
  }

  const CanvasRef = useRef();
  const hiddenFileInput = useRef(null);
  const PhotoGridScrollRef = useRef(null);
  const initializedRef = useRef(false);

  // 서버에서 받은 기존 사진 URL 배열로 초기 1회 초기화 //
  useEffect(() => {
    if (initializedRef.current) return;
    if (!initialPictures || initialPictures.length === 0) return;

    const initPics = initialPictures
      .filter(url => typeof url === 'string' && url !== '')
      .map(url => ({
        id: makePictureId(),
        original: url,
        edit: url,
        division: 'Original',
      }));

    if (initPics.length === 0) return;
    setPictures(initPics);
    initializedRef.current = true;
  }, [initialPictures]);

  // 드래그 시작 임계값을 두어 클릭(편집/삭제 버튼)이 정상 동작하도록 함 //
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = pictures.findIndex(p => p.id === active.id);
    const newIndex = pictures.findIndex(p => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(pictures, oldIndex, newIndex);
    setPictures(reordered);
    // 현재 선택된 사진의 인덱스도 새 순서에 맞춰 갱신
    if (picture) {
      const newPicIdx = reordered.findIndex(p => p.id === picture.id);
      if (newPicIdx >= 0) setPictureIndex(newPicIdx);
    }
  }

  useImperativeHandle(ref, () => ({
    getPictures: () => pictures.map(p => p.edit),
  }))

  const onClickImage = () => {
    hiddenFileInput.current.click();
  };

  // 단일 파일 → 리사이즈된 picture 객체 (Promise) //
  const processFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.toString();
        const naturalImage = new Image();
        naturalImage.src = base64;
        naturalImage.onload = (ev) => {
          const naturalWidth = ev.target.naturalWidth;
          const naturalHeight = ev.target.naturalHeight;

          if (naturalWidth > 1080) {
            const resizeWidth = 1080;
            const resizeHeight = (1080 * naturalHeight) / naturalWidth;

            const canvas = document.createElement('canvas');
            canvas.width = resizeWidth;
            canvas.height = resizeHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(null); return; }

            const image = new Image();
            image.src = base64;
            image.onload = () => {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(image, 0, 0, resizeWidth, resizeHeight);
              resolve({ id: makePictureId(), original: base64, edit: canvas.toDataURL(), division: 'Original' });
            };
          }
          else {
            resolve({ id: makePictureId(), original: base64, edit: base64, division: 'Original' });
          }
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const onChangeImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validExts = ['jpg', 'jpeg', 'png'];
    const validFiles = files.filter((f) => {
      const ext = f.name.split('.').pop().toLowerCase();
      return validExts.includes(ext);
    });

    if (validFiles.length === 0) {
      alert(t('noticePage.alertInvalidFileType'));
      e.target.value = '';
      return;
    }

    // 10장 제한: 추가 가능한 만큼만 처리 //
    const remaining = 10 - pictures.length;
    const filesToProcess = validFiles.slice(0, remaining);

    const processed = [];
    for (const file of filesToProcess) {
      const result = await processFile(file);
      if (result) processed.push(result);
    }

    if (processed.length === 0) {
      e.target.value = '';
      return;
    }

    const merged = [...pictures, ...processed];
    setPictures(merged);
    setPicture(merged[merged.length - 1]);
    setPictureIndex(merged.length - 1);

    e.target.value = '';
  };

  // 그리드 카드 편집 클릭 //
  function onClickEditAt(idx) {
    setPicture(pictures[idx]);
    setPictureIndex(idx);
    setIsClickedEditPhoto(true);
  }

  // 그리드 카드 삭제 클릭 //
  function onClickDeleteAt(idx) {
    const newPictures = pictures.filter((_, i) => i !== idx);
    setPictures(newPictures);

    if (newPictures.length === 0) {
      setPicture(undefined);
      setPictureIndex(0);
      setIsClickedEditPhoto(false);
      return;
    }

    if (idx <= pictureIndex) {
      const newIdx = Math.max(0, pictureIndex - 1);
      setPictureIndex(newIdx);
      setPicture(newPictures[newIdx]);
    }
  }

  // 모달 내부 이전 버튼 //
  function onClickPrevPicture() {
    if (pictureIndex > 0) {
      setPicture(pictures[pictureIndex - 1]);
      setPictureIndex(pictureIndex - 1);
    }
  }

  // 모달 내부 다음 버튼 //
  function onClickNextPicture() {
    if (pictureIndex < pictures.length - 1) {
      setPicture(pictures[pictureIndex + 1]);
      setPictureIndex(pictureIndex + 1);
    }
  }

  // 모달 내부 삭제 버튼 (현재 인덱스) //
  function onClickDeletePicture() {
    onClickDeleteAt(pictureIndex);
  }

  return (
    <TopLevelWrapper>
      {
        isClickedEditPhoto &&
        <ImageEditModal
          picture={picture}
          setPicture={setPicture}
          pictures={pictures}
          setPictures={setPictures}
          pictureIndex={pictureIndex}
          setPictureIndex={setPictureIndex}
          setNoEditPictureWidth={setPictureWidth}
          setNoEditPictureHeight={setPictureHeight}
          onClickExit={() => setIsClickedEditPhoto(false)}
          onClickPrevPicture={onClickPrevPicture}
          onClickNextPicture={onClickNextPicture}
          onClickDeletePicture={onClickDeletePicture}/>
      }
      {!readOnly && <canvas ref={CanvasRef} style={{ display: 'none' }} />}
      {!readOnly && (
        <input
          type="file"
          ref={hiddenFileInput}
          onChange={onChangeImage}
          multiple
          accept="image/jpeg,image/jpg,image/png"
          style={{ display: 'none' }} />
      )}
      {
        !readOnly && pictures.length === 0 &&
        <PictureLoadigButton onClick={onClickImage}>
          <PictureIcon />
        </PictureLoadigButton>
      }
      {
        pictures.length > 0 &&
        <PhotoSectionWrapper>
          <PhotoGridScrollRow>
            <PhotoGrid ref={PhotoGridScrollRef}>
              {
                readOnly ?
                  pictures.map((p) => (
                    <ViewPhotoCard key={p.id}>
                      <PhotoCardInner>
                        <PhotoImageWrapper $aspectRatio={photoRatios[p.edit] || 1}>
                          <PhotoImage src={p.edit} draggable={false} onLoad={(e) => handlePhotoLoad(p.edit, e)} />
                        </PhotoImageWrapper>
                      </PhotoCardInner>
                    </ViewPhotoCard>
                  ))
                  :
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={pictures.map(p => p.id)} strategy={rectSortingStrategy}>
                      {
                        pictures.map((p, idx) => (
                          <SortablePhotoCard
                            key={p.id}
                            id={p.id}
                            picture={p}
                            aspectRatio={photoRatios[p.edit] || 1}
                            theme={theme}
                            t={t}
                            onLoadPhoto={(e) => handlePhotoLoad(p.edit, e)}
                            onClickEdit={() => onClickEditAt(idx)}
                            onClickDelete={() => onClickDeleteAt(idx)} />
                        ))
                      }
                    </SortableContext>
                  </DndContext>
              }
            </PhotoGrid>
            <ScrollbarHolder>
              <CustomScrollbar
                direction='vertical'
                scrollRef={PhotoGridScrollRef}
                showTrackAlways={true}
                IsColorScrollBar={true}
                isTrackThin={true}
                isTumbThin={true}
                thumbThickness={4} />
            </ScrollbarHolder>
          </PhotoGridScrollRow>
          {
            !readOnly && pictures.length < 10 &&
            <AddPhotoCard onClick={onClickImage}>
              <StyledPlusButtonIcon />
            </AddPhotoCard>
          }
        </PhotoSectionWrapper>
      }
    </TopLevelWrapper>
  )
})

export default NoticePictureInput;

function SortablePhotoCard(props) {
  const { id, picture, aspectRatio, theme, t, onLoadPhoto, onClickEdit, onClickDelete } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 'auto',
    touchAction: 'none',
  };

  function onClickEditButton(e) {
    e.stopPropagation();
    onClickEdit();
  }

  function onClickDeleteButton(e) {
    e.stopPropagation();
    onClickDelete();
  }

  return (
    <PhotoCard ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <PhotoCardInner>
        <PhotoImageWrapper $aspectRatio={aspectRatio}>
          <PhotoImage src={picture.edit} draggable={false} onLoad={onLoadPhoto} />
        </PhotoImageWrapper>
      </PhotoCardInner>
      <CardOverlay>
        <PictureEditButton onClick={onClickEditButton}>
          <PencilIcon src={WhitePencelIcon} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.white} cursor='pointer'>{t('noticePage.editPhoto')}</Text>
        </PictureEditButton>
        <ExitButton onClick={onClickDeleteButton}>
          <ExitIcon />
        </ExitButton>
      </CardOverlay>
    </PhotoCard>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PictureLoadigButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 60px;
  height: 60px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PictureIcon = styled(ImageSvg)`
  width: 29px;
  height: 29px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.line};
  }
`;

const PhotoSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 10px;

  box-sizing: border-box;
  width: 344px;

  border: 1px solid ${props => props.theme.primaryAlpha20};
  border-radius: 5px;

  gap: 10px;
`;

const PhotoGridScrollRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  width: 100%;
  max-height: 622px;
`;

const ScrollbarHolder = styled.div`
  position: relative;
  z-index: 2;

  display: flex;
  align-items: stretch;

  flex-shrink: 0;
`;

const PhotoGrid = styled.div`
  display: grid;
  /* minmax(0, 1fr)로 img의 자연 폭(intrinsic min-content)이 컬럼을 그리드 폭 너머로 늘리는 것을 막음 */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: max-content;
  align-content: start;

  flex: 1;
  min-width: 0;
  min-height: 0;

  overflow-y: auto;
  overflow-x: hidden;

  gap: 10px;

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const PhotoCard = styled.div`
  position: relative;

  width: 100%;

  border-radius: 10px;

  overflow: hidden;

  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  /* padding-bottom 트릭으로 정사각형 보장 (grid + overflow 환경에서 aspect-ratio보다 신뢰성 높음) */
  &::before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`;

const ViewPhotoCard = styled.div`
  position: relative;

  width: 100%;

  border-radius: 10px;

  overflow: hidden;

  &::before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`;

const PhotoCardInner = styled.div`
  position: absolute;
  inset: 0;

  display: flex;

  justify-content: center;
  align-items: center;
`;

const PhotoImageWrapper = styled.div`
  display: flex;

  box-sizing: border-box;
  max-width: 100%;
  max-height: 100%;

  aspect-ratio: ${props => props.$aspectRatio};

  border-radius: 5px;

  overflow: hidden;
`;

const PhotoImage = styled.img`
  display: block;

  width: 100%;
  height: 100%;

  object-fit: contain;

  /* 브라우저 기본 이미지 드래그/선택 방지 → 스크롤바 클릭이 가로채지지 않도록 함 */
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  box-sizing: border-box;
  width: 100%;

  padding: 8px;
`;

const PictureEditButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 54px;
  height: 24px;

  gap: 2px;

  border-radius: 5px;
  background-color: rgba(0, 0, 0, 0.60);

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PencilIcon = styled.img`
  width: 18px;
  height: 18px;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  border-radius: 20px;
  background-color: rgba(0, 0, 0, 0.6);

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ExitIcon = styled(ExitButtonSvg)`
  width: 9px;
  height: 9px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.white};
  }

  & rect {
    fill: ${props => props.theme.white};
  }
`;

const AddPhotoCard = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 100%;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.primaryAlpha20};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledPlusButtonIcon = styled(PlusButtonSvg)`
  width: 24px;
  height: 24px;

  & path {
    fill: ${props => props.theme.text};
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }
`;
